import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import moment from "moment";
import "moment/locale/es";
import { getSectionToUserURL, getNotesURL } from "../../utils/ConstantsURL";
import { useAuth } from "../../context/AuthContext";

moment.locale("es");

const DocumentPreviewPage = () => {
  const { idDocument } = useParams();
  const [searchParams] = useSearchParams();
  const nameDocument = searchParams.get("name") || "Documento";
  const requestedDate = searchParams.get("date");
  const previewDate =
    requestedDate && moment(requestedDate, "YYYY-MM-DD", true).isValid()
      ? requestedDate
      : moment().format("YYYY-MM-DD");
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [documentData, setDocumentData] = useState([]);

  useEffect(() => {
    if (token && idDocument) {
      loadDocumentData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, idDocument, previewDate]);

  const loadDocumentData = async () => {
    setLoading(true);
    try {
      // 1. Fetch Sections
      const resSections = await fetch(getSectionToUserURL(idDocument), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resSections.ok) throw new Error("Error fetching sections");
      const sections = await resSections.json();

      // 2. Fetch Notes for all Sections
      const fetchNotesPromises = sections.map(async (section) => {
        const resNotes = await fetch(getNotesURL(previewDate, section.ID_SECCION, idDocument), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resNotes.ok) return { ...section, notas: [] };
        const notas = await resNotes.json();
        
        // Ordenar notas
        const notasOrdenadas = notas.sort((a, b) => {
          const orderA = a.orden_editor !== undefined ? a.orden_editor : 999;
          const orderB = b.orden_editor !== undefined ? b.orden_editor : 999;
          return orderA - orderB;
        });

        return { ...section, notas: notasOrdenadas };
      });

      const sectionsWithNotes = await Promise.all(fetchNotesPromises);
      setDocumentData(sectionsWithNotes);
    } catch (error) {
      console.error("Error loading preview:", error);
    } finally {
      setLoading(false);
    }
  };

  const stripHtml = (html) => {
    if (!html) return "";
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh" bgcolor="#ffffff">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: "#ffffff", minHeight: "100vh", p: 4, display: "flex", justifyContent: "center" }}>
      <Box className="documento" sx={styles.documento}>
        
        {/* LOGOTIPO TIPO LOGIN */}
        <Box className="logo-container" sx={styles.logoContainer}>
          <Typography variant="h4" component="h1" fontWeight="bold" color="primary" gutterBottom>
            EDITOR{" "}
            <Typography component="span" sx={{ fontSize: "0.7em", color: "error.main", verticalAlign: "middle" }}>
              v.26
            </Typography>
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Sistema de Gestión Editorial (SIGEDI)
          </Typography>
        </Box>

        {/* INFORMACIÓN */}
        <Box className="informacion" sx={styles.informacion}>
          <Typography variant="h6" fontWeight="bold">
            {nameDocument}
          </Typography>
          <Typography variant="body1">
            {moment(previewDate, "YYYY-MM-DD").format("dddd, DD [de] MMMM [del] YYYY")}
          </Typography>
        </Box>

        {/* SECCIONES */}
        <Box className="secciones" sx={styles.secciones}>
          {documentData.map((seccion) => (
            <Box key={seccion.ID_SECCION} className="seccion" sx={styles.seccion}>
              <Box className="seccion-titulo" sx={styles.seccionTitulo}>
                {seccion.NOMBRE_SECCION}
              </Box>

              {seccion.notas && seccion.notas.length > 0 ? (
                <Box className="notas" sx={styles.notasContainer}>
                  {seccion.notas.map((nota) => (
                    <Box key={nota.id_sintesis} className="nota" sx={styles.nota}>
                      <Box className="nota-titulo" sx={styles.notaTitulo}>
                        {nota.titulo || nota.CAPTITULO}
                      </Box>
                      <Box className="nota-contenido" sx={styles.notaContenido}>
                        {stripHtml(nota.contenido || nota.sintesis || nota.CAPTEXTCOMP)}
                      </Box>
                      
                      {/* Testigos (Witnesses) */}
                      {nota.testigo && nota.testigo.length > 0 && (
                        <Box sx={styles.testigosContainer}>
                          {nota.testigo.map((ref, idx) => (
                            <React.Fragment key={idx}>
                              <a href={ref.url || "#"} target={ref.url ? "_blank" : "_self"} rel="noopener noreferrer" style={{ color: "#1976d2", textDecoration: "none" }}>
                                ({ref.medio || ref.testigo}{ref.autor ? `, ${ref.autor}` : ""})
                              </a>
                              {idx < nota.testigo.length - 1 && ", "}
                            </React.Fragment>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", marginLeft: "10px" }}>
                  Esta sección aún no tiene notas.
                </Typography>
              )}
            </Box>
          ))}
        </Box>

      </Box>
    </Box>
  );
};

const styles = {
  documento: {
    fontFamily: "Arial, sans-serif",
    width: "100%",
    maxWidth: "900px",
  },
  logoContainer: {
    textAlign: "center",
    marginBottom: "20px",
  },
  informacion: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  secciones: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  seccion: {
    width: "100%",
  },
  seccionTitulo: {
    fontSize: "1.2rem",
    fontWeight: "bold",
    color: "#ffffff",
    backgroundColor: "#1976d2", // Azul oscuro
    padding: "10px",
    textTransform: "uppercase",
    width: "100%",
    marginBottom: "10px",
    position: "sticky",
    top: 0,
    zIndex: 2,
  },
  notasContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
  },
  nota: {
    paddingBottom: "15px",
  },
  notaTitulo: {
    fontSize: "1.1rem",
    fontWeight: "bold",
    marginBottom: "5px",
  },
  notaContenido: {
    fontSize: "1rem",
    textAlign: "justify",
    marginBottom: "10px",
  },
  testigosContainer: {
    marginTop: "5px",
  }
};

export default DocumentPreviewPage;
